import { type EventError, type EventMessage, EventType, InteractionType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { readEnv } from "@repo/helpers";
import { ADB2C_ERROR_CODES, B2CPolicies, LocalStorageKeys } from "@repo/logic";
import { useQueryClient } from "@tanstack/react-query";
import { navigate } from "gatsby";
import { useEffect, useState } from "react";

import { setActivity } from "@repo/ui";

const defaultRequest = {
    scopes: ["openid", readEnv("GATSBY_B2C_API_SCOPE") ?? ""],
};

const isV2RegistrationEnabled = readEnv("GATSBY_FEATURE_IS_V2_REGISTRATION_ENABLED") === "true";

export const useLoginError = () => {
    const [loginError, setLoginError] = useState<EventError | undefined>(undefined);
    const [authority, setAuthority] = useState<string | undefined | null>(undefined);

    const { instance } = useMsal();
    const queryClient = useQueryClient();

    useEffect(() => {
        const auth = localStorage.getItem(LocalStorageKeys.AUTHORITY);
        if (authority) setAuthority(auth);
    }, []);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        const callbackId = instance.addEventCallback(async (event: EventMessage) => {
            if (
                [
                    EventType.LOGIN_FAILURE,
                    EventType.LOGOUT_FAILURE,
                    EventType.SSO_SILENT_FAILURE,
                    EventType.ACQUIRE_TOKEN_FAILURE,
                    EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE,
                ].includes(event.eventType)
            ) {
                if (event.error && event.error.errorMessage.indexOf(ADB2C_ERROR_CODES.USER_FORGOT_PASSWORD) > -1) {
                    if (event.interactionType === InteractionType.Redirect) {
                        const request = {
                            authority: `https://${readEnv("GATSBY_B2C_DOMAIN")}/${readEnv("GATSBY_B2C_TENANT_ID")}/${B2CPolicies.PasswordReset()}`,
                        };
                        await instance.loginRedirect({ ...defaultRequest, ...request });
                    }
                    return;
                }

                if (event.error && event.error.errorMessage.indexOf(ADB2C_ERROR_CODES.USER_CANCELED_OPERATION) > -1) {
                    return;
                }

                setLoginError(event.error);
            }

            if (
                [
                    EventType.LOGIN_SUCCESS,
                    EventType.ACQUIRE_TOKEN_SUCCESS,
                    EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS,
                    EventType.SSO_SILENT_SUCCESS,
                ].includes(event.eventType)
            ) {
                localStorage.removeItem(LocalStorageKeys.REACT_QUERY_OFFLINE_CACHE);
                queryClient.removeQueries();
                setActivity();
                instance.setActiveAccount(event.payload.account);

                if (
                    !isV2RegistrationEnabled &&
                    (!event.payload.account.idTokenClaims.extension_PID ||
                        !event.payload.account.idTokenClaims.extension_UserDetailInfoEncrypted)
                ) {
                    await instance.logout({
                        postLogoutRedirectUri: "/uzivatel-nenalezen/",
                    });
                } else if (event.payload.account.idTokenClaims.partner_code === "AirBank") {
                    navigate("/uvitaci-obrazovka-airbank/");
                } else {
                    navigate("/smlouvy/");
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance, queryClient]);

    return {
        loginError,
        authority,
    };
};
